import React, { FC, ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { Compose } from "@natera/platform/lib/components/compose";
import classNames from "classnames";

import {
  MpxProvider,
  SignUpProvider,
  SignInProvider,
  PasswordPolicyProvider,
  ResetPasswordProvider,
  ActivationModeProvider,
} from "@app/provider";
import { GradientWrapper, Header } from "@app/components";
import { routes } from "@app/routing";
import { PreBillContextWrapper } from "@app/preBill/preBillWrapper";
import { PostBillContextWrapper } from "@app/postBill/postBillWrapper";
import BillingMenuProvider from "@app/components/navigation/billingMenuContext";
import { trimTrailingSlashes } from "@app/utils/common";
import { UPSContextWrapper } from "@app/ups/upsWrapper";

import "./main.scss";

const Main: FC<{ children: ReactNode }> = ({ children }) => {
  const location = useLocation();
  const pathname = trimTrailingSlashes(location.pathname);

  const isFullWidth =
    [
      routes.activation,
      routes.resetPassword,
      routes.sendEmail,
      routes.setPassword,
      routes.scheduleBloodDraw,
      routes.guestLogin,
      routes.nonCoveredService,
      routes.billingEstimate,
      routes.outOfNetwork,
      routes.priorAuthorization,
      routes.postbillGuestLogin,
      routes.billingCenterGuestLogin,
      routes.billingCenter,
      routes.reviewBills,
      routes.billingPayment,
      routes.billingFailure,
      routes.billingConfirmation,
      routes.noLongerAvailable,
      routes.insuranceNoLongerAvailable,
      routes.drawRequest,
      routes.signUp,
      routes.logIn,
      routes.loginRedirect,
      routes.rootPage,
      routes.home,
      routes.billingPaymentPlanDetail,
      routes.unlock,
      routes.renewalInterested,
      routes.upsGuestLogin,
      routes.upsUpdatePaymentMethod,
      routes.upsUpdatePaymentMethodSuccess,
      routes.upsReviewInvoice,
      routes.upsCreatePaymentMethod,
      routes.upsCreatePaymentConfirmation,
      routes.upsCreatePaymentFailure,
      routes.upsNoLongerAvailable,
      routes.upsReceipt,
      routes.billingUpdatePayment,
      routes.billingStatementPdf,
      routes.publicGisEntry,
      routes.publicGisOrderLookup,
    ]
      .map(trimTrailingSlashes)
      .includes(pathname) ||
    pathname.includes("/education/prenatal") ||
    pathname.includes("/intake") ||
    pathname.includes("/schedule_session") ||
    pathname.includes("/patient-education") ||
    // Reached from the guest GIS education step, and laid out like the rest of
    // that funnel rather than in the narrow column the sign-in forms use.
    pathname.includes("/understanding-your-results");

  const isDisplayGradientWrapper =
    [
      routes.rootPage,
      routes.signUp,
      routes.logIn,
      routes.loginRedirect,
      routes.home,
      routes.activation,
      routes.resetPassword,
      routes.sendEmail,
      routes.setPassword,
      routes.scheduleBloodDraw,
      routes.unlock,
      routes.publicGisEntry,
    ]
      .map(trimTrailingSlashes)
      .includes(pathname) || pathname.includes("/education/prenatal");

  const isDisableGradientForMobile =
    [routes.rootPage, routes.signUp, routes.logIn]
      .map(trimTrailingSlashes)
      .includes(pathname) || pathname.includes("/education/prenatal");

  return (
    <BillingMenuProvider>
      <Header isWidthLimited={!isFullWidth} />
      <main
        className={classNames({
          "public-main": !isFullWidth,
          "public-main--full-width": isFullWidth,
        })}
      >
        <Compose
          components={[
            SignUpProvider,
            SignInProvider,
            PasswordPolicyProvider,
            ActivationModeProvider,
            ResetPasswordProvider,
            MpxProvider,
            PreBillContextWrapper,
            UPSContextWrapper,
            PostBillContextWrapper,
          ]}
        >
          <GradientWrapper
            display={isDisplayGradientWrapper}
            disableForMobile={isDisableGradientForMobile}
          >
            {children}
          </GradientWrapper>
        </Compose>
      </main>
    </BillingMenuProvider>
  );
};

export default Main;
